import type { BoxProps } from "@qws-org/ui-kit";
import { Box, Button, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Call_To_Action, Pages } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";

export type ImageParams = {
  format?:
    | "jpg"
    | "jpeg"
    | "png"
    | "gif"
    | "webp"
    | "avif"
    | "svg"
    | "bmp"
    | "ico"
    | "tiff";
  height?: number;
  width?: number;
};

export type CallToActionProps = {
  data: Call_To_Action;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isActionInactive: (action: Action) => boolean;
} & BoxProps;

export const CallToActionBlock: FC<CallToActionProps> = ({
  data,
  getImageUrl,
  getUrl,
  onCallAction,
  isActionInactive,
  ...rest
}) => {
  const {
    variant,
    action,
    backroundImage,
    backgroundColor,
    image,
    picture,
    action_text,
    picture_mobile_margin_left,
    picture_mobile_margin_top,
    picture_desktop_margin_top,
    picture_tablet_margin_top,
    picture_desktop_margin_left,
    picture_tablet_margin_left,
  } = data;

  const actionItem = action?.action?.[0]?.item;
  let href;
  if (isExternalLink(actionItem)) {
    href = actionItem.href ?? undefined;
  }
  if (isInternalLink(actionItem)) {
    href = actionItem.page ? getUrl(actionItem.page) : undefined;
  }
  const mobileImage = picture?.image;
  const mobileColor = picture?.color ?? undefined;
  const mobileWidth = picture?.mobile_image_width ?? undefined;
  const mobileHeight = picture?.mobile_image_height ?? undefined;

  const tabletImage = picture?.tablet_image ?? mobileImage;
  const tabletColor = picture?.tablet_color ?? mobileColor;
  const tabletWidth = picture?.tablet_image_width ?? mobileWidth;
  const tabletHeight = picture?.tablet_image_height ?? mobileHeight;

  const desktopImage = picture?.desktop_image ?? tabletImage;
  const desktopColor = picture?.desktop_color ?? tabletColor;
  const desktopWidth = picture?.desktop_image_width ?? tabletWidth;
  const desktopHeight = picture?.desktop_image_height ?? tabletHeight;

  const pictureMobileMarginTop = picture_mobile_margin_top ?? 0;
  const pictureMobileMarginLeft = picture_mobile_margin_left ?? 0;
  const pictureTabletMarginTop =
    picture_tablet_margin_top ?? pictureMobileMarginTop;
  const pictureTabletMarginLeft =
    picture_tablet_margin_left ?? pictureMobileMarginLeft;
  const pictureDesktopMarginTop =
    picture_desktop_margin_top ?? pictureTabletMarginTop;
  const pictureDesktopMarginLeft =
    picture_desktop_margin_left ?? pictureTabletMarginLeft;

  return (
    <Box
      bg={
        backroundImage
          ? getImageUrl(backroundImage.id, { width: 400, format: "webp" })
          : (backgroundColor ?? "")
      }
      {...rest}
      md={{
        bg: backroundImage
          ? getImageUrl(backroundImage.id, { width: 798, format: "webp" })
          : (backgroundColor ?? ""),
        ...rest.md,
      }}
      d={{
        bg: backroundImage
          ? getImageUrl(backroundImage.id, { width: 1920, format: "webp" })
          : (backgroundColor ?? ""),
        ...rest.d,
      }}
      data-testid="call-to-action"
    >
      <Flex
        align={"center"}
        maxWidth={1270}
        p={{ px: 15 }}
        m={{ mx: "auto" }}
        gap={30}
        md={{
          gap: variant === "column" ? 0 : 50,
          flexDirection:
            variant === "leftToRight"
              ? "row"
              : variant === "rightToLeft"
                ? "row-reverse"
                : "column",
          maxHeight: variant === "column" ? "fit-content" : 230,
        }}
        d={{
          gap: variant === "column" ? 0 : 150,
          maxHeight: variant === "column" ? "fit-content" : 230,
        }}
        flexDirection="column"
      >
        {picture && mobileImage ? (
          <Image
            value={
              mobileImage?.id
                ? getImageUrl(mobileImage.id, {
                    height: mobileHeight,
                    width: mobileWidth,
                    format: "webp",
                  })
                : (mobileColor ?? "")
            }
            width={mobileWidth}
            height={mobileHeight}
            m={{
              mt: pictureMobileMarginTop,
              ml: pictureMobileMarginLeft,
            }}
            mdValue={
              tabletImage?.id
                ? getImageUrl(tabletImage.id, {
                    height: tabletHeight,
                    width: tabletWidth,
                    format: "webp",
                  })
                : (tabletColor ?? mobileColor)
            }
            md={{
              width: tabletWidth,
              height: tabletHeight,
              m: { mt: pictureTabletMarginTop, ml: pictureTabletMarginLeft },
            }}
            dValue={
              desktopImage?.id
                ? getImageUrl(desktopImage.id, {
                    height: desktopHeight,
                    width: desktopWidth,
                    format: "webp",
                  })
                : (desktopColor ?? tabletColor)
            }
            d={{
              width: desktopWidth,
              height: desktopHeight,
              m: { mt: pictureDesktopMarginTop, ml: pictureDesktopMarginLeft },
            }}
          />
        ) : (
          image?.id && (
            <Image
              m={{ mt: -24 }}
              md={{ m: { mt: -28 }, minWidth: 420 }}
              d={{ minWidth: 470 }}
              dValue={getImageUrl(image?.id ?? "", {
                width: 546,
                height: 350,
                format: "webp",
              })}
              mdValue={getImageUrl(image?.id ?? "", {
                width: 550,
                height: 350,
                format: "webp",
              })}
              value={getImageUrl(image?.id ?? "", {
                width: 310,
                height: 200,
                format: "webp",
              })}
            />
          )
        )}

        <Flex
          flexDirection="column"
          align="center"
          maxWidth="50%"
          p={{ pb: 48 }}
          md={{
            flexDirection: "column",
            align: variant === "column" ? "center" : "end",
            p: {
              py: 36,
            },
          }}
        >
          <Typography
            maxWidth={540}
            as="p"
            color="text-primary-inverted"
            fontSize="body.mobile.large"
            md={{
              fontSize: "body.desktop.large",
              horizontalAlign:
                variant === "leftToRight"
                  ? "right"
                  : variant === "rightToLeft"
                    ? "left"
                    : "center",
            }}
            fontWeight="desktop.bold"
            horizontalAlign="center"
            data-testid="call-to-action-text"
          >
            {data.text}
          </Typography>
          {action && !isActionInactive(action) && (
            <Button
              size="small"
              weight="bold"
              type="submit"
              variant="accent"
              m={{ mt: 32 }}
              md={{ p: { px: 48, py: 17 } }}
              p={{ px: 24, py: 12 }}
              href={href}
              onPress={() => {
                if (action) {
                  onCallAction(action);
                }
              }}
              data-testid="call-to-action-button"
            >
              {action_text}
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
